import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from '@/lib/api';
import i18n from "@/i18n";
import { getSessionToken, setSessionToken, clearSessionToken } from "@/lib/api";
import { BACKEND_URL } from "../config";
const AuthContext = createContext({
  user: null,
  loading: true,
  isAuthenticated: false,
  isProfileComplete: true,
  showProfileModal: false,
  setShowProfileModal: () => {},
  login: () => {},
  logout: () => {},
  refreshUser: () => {},
  requireAuth: () => false,
});

export const useAuth = () => useContext(AuthContext);

// Helper function to check if profile is complete
const checkProfileComplete = (userData) => {
  if (!userData) return true; // Not logged in = no need to complete
  const hasLocationFields = userData.country && userData.region;
  const hasBasicFields = userData.name && userData.nickname;
  return hasBasicFields && hasLocationFields;
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = getSessionToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const USER_CACHE_KEY = 'mototrips_user_cache';

const cacheUser = (userData) => {
  try {
    if (userData) {
      sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
    } else {
      sessionStorage.removeItem(USER_CACHE_KEY);
    }
  } catch {}
};

const getCachedUser = () => {
  try {
    const cached = sessionStorage.getItem(USER_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  // Initialize user from cache immediately (no flash of "not logged in")
  const [user, setUserRaw] = useState(() => {
    const token = getSessionToken();
    return token ? getCachedUser() : null;
  });
  const [loading, setLoading] = useState(() => {
    // If we have a cached user, skip loading state entirely
    const token = getSessionToken();
    return token ? !getCachedUser() : false;
  });
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // Ref to prevent double execution in StrictMode
  const hasInitialized = useRef(false);

  // Wrap setUser to also update cache
  const setUser = useCallback((userData) => {
    setUserRaw(userData);
    cacheUser(userData);
  }, []);

  const isAuthenticated = !!user;
  const isProfileComplete = checkProfileComplete(user);

  // Apply user's preferred language - only if different from current
  const applyUserLanguage = useCallback((userData) => {
    if (userData?.preferred_language) {
      const currentLang = i18n.language?.substring(0, 2);
      const userLang = userData.preferred_language.substring(0, 2);
      // Only change if actually different to avoid unnecessary re-renders
      if (currentLang !== userLang) {
        i18n.changeLanguage(userData.preferred_language);
      }
    }
    // If no preferred_language, keep whatever language is currently active
    // (from localStorage or browser detection)
  }, []);

  // Check authentication only on initial mount - use ref to prevent StrictMode double execution
  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    
    const checkAuth = async () => {
      const token = getSessionToken();
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const response = await api.get("/api/auth/me", {
          headers: getAuthHeaders()
        });
        setUser(response.data);
        
        // Apply language preference - only if different from current
        if (response.data?.preferred_language) {
          const currentLang = i18n.language?.substring(0, 2);
          const userLang = response.data.preferred_language.substring(0, 2);
          if (currentLang !== userLang) {
            i18n.changeLanguage(response.data.preferred_language);
          }
        }
        
        // Check if profile is complete and show modal if not
        if (response.data && !checkProfileComplete(response.data)) {
          setShowProfileModal(true);
        }
      } catch (error) {
        // Only clear user on explicit 401 (invalid/expired token)
        // Network errors (backend down during deploy) should preserve current state
        if (error.response?.status === 401) {
          clearSessionToken();
          setUser(null);
        }
        // If no response at all (network error), keep user state as-is
        // The BackendHealthMonitor will handle redirect to maintenance
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get("/api/auth/me", {
        headers: getAuthHeaders()
      });
      setUser(response.data);
      
      // Update profile modal state based on completeness
      if (response.data && !checkProfileComplete(response.data)) {
        setShowProfileModal(true);
      } else {
        setShowProfileModal(false);
      }
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        clearSessionToken();
        setUser(null);
      }
      // Network errors: keep current user state
      return null;
    }
  }, []);

  const login = useCallback((userData, sessionToken) => {
    setUser(userData);
    // Store the session token if provided
    if (sessionToken) {
      setSessionToken(sessionToken);
    }
    applyUserLanguage(userData);
    
    // Check if profile is complete and show modal if not
    if (userData && !checkProfileComplete(userData)) {
      setShowProfileModal(true);
    }
  }, [applyUserLanguage]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout", {}, { 
        headers: getAuthHeaders()
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear stored token and cached user BEFORE redirect
    // but do NOT update React state (setUserRaw) to avoid
    // ProtectedRoute flashing /regista-te before the reload
    clearSessionToken();
    cacheUser(null);
    setShowProfileModal(false);
    // Full page reload to /entrada — clears all React state
    window.location.href = "/";
  }, []);

  // Listen for auth:expired events from API interceptors
  useEffect(() => {
    const handleExpired = () => {
      cacheUser(null);
      setUserRaw(null);
      setShowProfileModal(false);
      window.location.href = "/login";
    };
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, []);

  // Function to require auth - redirects to join page if not authenticated
  // Returns true if authenticated, false if redirecting
  const requireAuth = (message = "Para aceder a esta funcionalidade, precisas de criar uma conta.") => {
    if (isAuthenticated) {
      return true;
    }
    // Store the redirect info for navigation
    setPendingRedirect({ message });
    return false;
  };

  // Get and clear pending redirect
  const getPendingRedirect = () => {
    const redirect = pendingRedirect;
    setPendingRedirect(null);
    return redirect;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      isProfileComplete,
      showProfileModal,
      setShowProfileModal,
      login,
      logout,
      refreshUser,
      requireAuth,
      getPendingRedirect,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
